import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import { auditIndexQuery, type AuditIndex } from '../../api/audit'
import { moduleOf, shellQuery } from '../../api/shell'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { AuditGrid, areaMatches } from './AuditGrid'

/**
 * IT Audit - catalogul, desenat ca ecranele CMDB si de descoperire: macheta
 * `views/version-3/settings/audit/index.php`, element cu element.
 *
 * O pagina, fara rail de setari: numele, cautarea, apoi cele zece tipuri ca
 * zece carduri mari, fiecare cu ariile lui. Toate cele o suta de arii sunt in
 * pagina de la inceput; cautarea ingusteaza, nu aduce. Nimic nu se masoara
 * aici si nimic nu se preface: rularile stau pe pagina fiecarui tip.
 */

/** Foile pe care le cerea pagina veche: plugin-ul 'adm' si foaia auditului. */
export const AUDIT_STYLES = ['css/adm.css', 'css/audit.css']

function PageHead({ page, needle, onFind, shown }: { page: AuditIndex; needle: string; onFind: (v: string) => void; shown: number }) {
  const overall = page.overall
  const stats = [
    {
      value: overall.mean !== null ? overall.mean.toFixed(1) : '—',
      label: overall.mean !== null ? `${overall.serious} critical or high` : 'no overall yet',
    },
    { value: String(page.counts.types), label: 'audit types' },
    { value: String(page.counts.areas), label: 'areas' },
  ]
  return (
    <div className="adm-page-head adm-tone-blue-mauve">
      <span className="adm-page-icon">
        <i className="fas fa-clipboard-check"></i>
      </span>
      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>IT Audit Catalogue</h3>
        </div>
        <span className="adm-page-note">what an IT audit looks at, type by type - open a type to see its plan and run it</span>
      </div>
      <div className="adm-page-stats">
        {stats.map((stat) => (
          <div className="adm-stat adm-stat-blue-mauve" key={stat.label}>
            <span className="adm-stat-value">{stat.value}</span>
            <span className="adm-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
      <div className="adm-page-actions">
        <a className="btn btn-default" href="/app/settings/audit/prompts">
          <i className="fas fa-comment-dots" aria-hidden="true"></i> Briefing
        </a>
        <a className="btn btn-default" href="/app/settings/audit/register">
          <i className="fas fa-clipboard-list" aria-hidden="true"></i> Findings
        </a>
        <div className="od-audit-find">
          <label className="sr-only" htmlFor="auditFind">
            Find an area across all ten types
          </label>
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            type="search"
            className="form-control input-sm"
            id="auditFind"
            autoComplete="off"
            placeholder="Find an area: restore, MFA, CMDB, segregation, RTO..."
            value={needle}
            onChange={(e) => onFind(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onFind('')
            }}
          />
          <span className="adm-count" id="auditShown">
            {shown} areas shown
          </span>
        </div>
      </div>
    </div>
  )
}

export function AuditIndexPage() {
  const location = useLocation()
  const shell = useQuery(shellQuery(moduleOf(location.pathname)))
  const page = useQuery(auditIndexQuery)
  const [needle, setNeedle] = useState('')
  const [mapOpen, setMapOpen] = useState(false)
  usePageStyles(AUDIT_STYLES, shell.data?.version ?? '')

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data
  const q = needle.trim().toLowerCase()
  const shown = data.types.reduce((n, t) => n + t.areas.filter((a) => areaMatches(a, q)).length, 0)

  return (
    <Content>
      <div className="od-audit">
        <PageHead page={data} needle={needle} onFind={setNeedle} shown={shown} />

        {data.overall.mean === null && data.overall.why !== '' && (
          <p className="od-audit-nooverall">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No overall rating: {data.overall.why}.
          </p>
        )}

        <AuditGrid
          types={data.types}
          runCounts={data.runCounts}
          lastRuns={data.lastRuns}
          ratings={data.ratings}
          sides={data.sides}
          needle={needle}
        />

        <div className="adm-empty-inline od-audit-none" hidden={shown > 0}>
          <i className="fas fa-search"></i> Nothing in the catalogue mentions that. The areas are written in the auditor's vocabulary - try a
          shorter word.
        </div>

        <div className="od-audit-map-fold">
          <button
            type="button"
            className="od-audit-spec-toggle"
            aria-expanded={mapOpen}
            aria-controls="auditCrossMap"
            onClick={() => setMapOpen(!mapOpen)}
          >
            <i className="fas fa-chevron-right" aria-hidden="true"></i>
            <span>
              <i className="fas fa-random" aria-hidden="true"></i> Topics assessed in more than one domain, each from a different lens
            </span>
          </button>
        </div>
        <div className="adm-card od-audit-card od-audit-map" id="auditCrossMap" hidden={!mapOpen}>
          <table className="table od-audit-table">
            <thead>
              <tr>
                <th className="od-audit-th-area">Topic</th>
                <th>Where it is covered (area · lens)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.crossMap).map(([topic, where]) => (
                <tr key={topic}>
                  <td className="od-audit-title">{topic}</td>
                  <td className="adm-muted">{where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Content>
  )
}
